import type { SittingMilestone } from '../lib/sittingTimeline'
import type { ExamSitting } from './examSittings'
import { feeFact } from './examFees'

/**
 * **What the examining bodies publish about each sitting**, beyond the window
 * `data/examSittings.ts` already carries: when registration opens and closes,
 * PCPA's exam deadline and project submission deadline, and when results come
 * out. Read by the study guide's info button
 * (`components/wiki/ExamSittingInfoButton.tsx`) through `lib/sittingTimeline.ts`.
 *
 * ── Rules for this file ─────────────────────────────────────────────────────
 * The same rule as `examPdfLinks.ts` and the pass-rate tables: a date here is
 * **transcribed from the publisher, never constructed**. CAS and SOA both keep
 * a regular-looking calendar, and both break it — a results date that "is
 * always about eight weeks later" is exactly the guess this file exists to
 * keep out. A date that hasn't been published is simply **absent**, and the
 * info panel then shows the dates that are, and nothing in the gap.
 *
 * Every entry names the page its dates came from (`sources`), and the panel
 * links it, so a candidate can check the date against the publisher before
 * booking anything on it. A qualifier the publisher attaches ("subject to
 * change") rides on the milestone as its `note`, in the publisher's words.
 */

export interface SittingSource {
  /** What the link is, as the reader will see it — "CAS — Exam Schedule". */
  label: string
  url: string
}

export interface SittingDetails {
  /**
   * The exams (exam_progress keys — `P`, `CAS-5`, `CAS-PCPA`) the entry
   * covers. CAS runs one calendar for all its upper-level exams, so one entry
   * can serve several.
   */
  examIds: string[]
  /** The window's first day — the `startDate` of the sittings row it details. */
  startDate: string
  /**
   * Only for a sitting offered in two formats that start the same day (SOA's
   * CBT window and paper day), where each has its own dates.
   */
  format?: ExamSitting['format']
  milestones: SittingMilestone[]
  /** Anything else the publisher says about this sitting, in its words. */
  notes?: string[]
  sources: SittingSource[]
}

/** What stays the same from sitting to sitting: how the exam is sat. */
export interface ExamAbout {
  /** Label / value rows — "Length" / "4 hours (4.5-hour appointment)". */
  facts: { label: string; value: string }[]
  /** The examining body's own page for the exam — always somewhere to go. */
  source: SittingSource
  /** The documents the facts were read from, where not the page itself. */
  factSources?: SittingSource[]
  /**
   * For a requirement that has no sittings at all, why — in place of the
   * panel's "no upcoming sitting has been published yet", which would read
   * as a gap in the calendar rather than the absence of one.
   */
  noSittings?: string
}

// ── Sources ───────────────────────────────────────────────────────────────────
// Every page below was read on 2026-09-27.

const CAS_EXAM = 'https://www.casact.org/exam/'
const SOA_EXAM = 'https://www.soa.org/education/exam-req/'

const CAS_SOBE: SittingSource = {
  // "Revision 1, 6/26/2026" of the 2026 Syllabus — despite the filename. It
  // says of itself that its dates "take precedence over all other sources,
  // including the CAS website".
  label: 'CAS — 2026 Syllabus of Basic Education',
  url: 'https://www.casact.org/sites/default/files/2025-01/2025_SOBE.pdf',
}
const CAS_OCT_2026_REGISTRATION: SittingSource = {
  label: 'CAS — October/November 2026 registration',
  url: 'https://www.casact.org/article/registration-now-open-octobernovember-cas-exams',
}
const CAS_PCPA: SittingSource = {
  label: 'CAS — PCPA',
  url: `${CAS_EXAM}property-casualty-predictive-analytics-pcpa`,
}
const CAS_PCPA_OUTLINE: SittingSource = {
  // v.8, "Updated: 9.9.2026", "Effective: September 2026". Its page 2 is the
  // "Project Administration Details" table: exam deadline, registration
  // deadline, window, submission deadline, results — with no year printed.
  label: 'CAS — PCPA content outline',
  url: 'https://www.casact.org/sites/default/files/2024-05/Exam_PCPA_2025_F_Content_Outlines.pdf',
}
const CAS_DISC: SittingSource = {
  label: 'CAS — Data and Insurance Series Courses',
  url: 'https://www.casact.org/exams-admissions/exams/acas-exams/cas-data-and-insurance-series-courses',
}
const CAS_FEES: SittingSource = {
  label: 'CAS — Exam fees',
  url: 'https://www.casact.org/exams-admissions/exams/exam-fees',
}

const SOA_SCHEDULE: SittingSource = {
  label: 'SOA — Exam schedule',
  url: 'https://www.soa.org/education/exam-schedule/',
}
const SOA_RESULTS: SittingSource = {
  // The "Release Schedule" tab: candidate-number and transcript dates.
  label: 'SOA — Exam results',
  url: 'https://www.soa.org/education/exam-results/',
}
const SOA_FEES: SittingSource = {
  label: 'SOA — Exam fees',
  url: 'https://www.soa.org/education/exam-req/syllabus-study-materials/exam-and-module-fees/',
}

// ── How each exam is sat ──────────────────────────────────────────────────────

// CAS's content outlines give the appointment and the item types; none gives a
// question count ("the total number of questions may vary from one exam sitting
// to the next" — the Syllabus), so none is shown.
const CAS_LENGTH = { label: 'Length', value: '4 hours (4.5-hour appointment)' }
const CAS_DELIVERY = { label: 'Delivery', value: 'Pearson VUE test centre' }
const CAS_MAS_ITEMS = {
  label: 'Question types',
  value: 'Multiple choice, multiple selection, point and click, fill in the blank, matching',
}
const CAS_UPPER_ITEMS = {
  label: 'Question types',
  value: 'Constructed response and spreadsheet, with multiple choice, multiple selection, point and click, fill in the blank and matching',
}
// The fees are `data/examFees.ts`'s — one table, read by this panel and the
// Store's registration aisle alike.
const CAS_UPPER_FEE = feeFact('CAS-5')

function casOutline(exam: string, url: string): SittingSource {
  return { label: `CAS — ${exam} content outline`, url }
}

// SOA's syllabi: "a three-hour exam that consists of 30 multiple-choice
// questions" (P), "a 2.5-hour exam that consists of 30 multiple-choice
// questions" (FM), and "Unofficial pass/fail results will be sent within one
// hour to the email address you used to schedule your appointment."
const SOA_UNOFFICIAL = { label: 'Unofficial result', value: 'Emailed within an hour of a CBT exam' }
const SOA_DELIVERY = { label: 'Delivery', value: 'Prometric test centre; paper and pencil at select international centres' }

// The DISCs, from CAS's DISC page: "Candidates taking the virtual online
// courses will have 100 minutes to complete 75 application-based
// multiple-choice questions", "The course fee includes one attempt at the
// exam", and The Institutes "will send a copy of the grades directly to the CAS
// Office at the conclusion of the test window ... This can take up to 10-15
// business days." The page names no test-window dates, so there is no sitting.
const DISC: ExamAbout = {
  facts: [
    { label: 'Exam', value: '75 multiple-choice questions in 100 minutes' },
    { label: 'Course', value: 'Online, with The Institutes' },
    { label: 'Fee', value: 'The course fee includes one exam attempt' },
    { label: 'Grades', value: 'Sent to CAS after the test window; can take 10–15 business days' },
  ],
  source: CAS_DISC,
  noSittings: 'The DISCs are online courses the CAS runs with The Institutes, and CAS publishes no sitting dates for them.',
}

export const EXAM_ABOUT: Record<string, ExamAbout> = {
  // ── SOA ───────────────────────────────────────────────────────────────────
  P: {
    facts: [
      { label: 'Length', value: '3 hours' },
      { label: 'Questions', value: '30 multiple choice' },
      SOA_DELIVERY,
      SOA_UNOFFICIAL,
      feeFact('P'),
    ],
    source: { label: 'SOA — Exam P', url: `${SOA_EXAM}edu-exam-p-detail/` },
    factSources: [
      { label: 'SOA — Exam P syllabus (November 2026)', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/fall/syllabi/2026-11-exam-p-syllabus.pdf' },
      SOA_FEES,
    ],
  },
  FM: {
    facts: [
      { label: 'Length', value: '2.5 hours' },
      { label: 'Questions', value: '30 multiple choice' },
      SOA_DELIVERY,
      SOA_UNOFFICIAL,
      feeFact('FM'),
    ],
    source: { label: 'SOA — Exam FM', url: `${SOA_EXAM}edu-exam-fm-detail/` },
    factSources: [
      { label: 'SOA — Exam FM syllabus (December 2026)', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/fall/syllabi/2026-12-exam-fm-syllabus.pdf' },
      SOA_FEES,
    ],
  },

  // ── CAS ───────────────────────────────────────────────────────────────────
  'MAS-I': {
    facts: [CAS_LENGTH, CAS_DELIVERY, CAS_MAS_ITEMS, feeFact('MAS-I')],
    source: { label: 'CAS — Exam MAS-I', url: `${CAS_EXAM}exam-mas-i-modern-actuarial-statistics-i` },
    factSources: [casOutline('MAS-I', 'https://www.casact.org/sites/default/files/2026-06/MASI_ContentOutline_2026.pdf'), CAS_FEES],
  },
  'MAS-II': {
    facts: [CAS_LENGTH, CAS_DELIVERY, CAS_MAS_ITEMS, feeFact('MAS-II')],
    source: { label: 'CAS — Exam MAS-II', url: `${CAS_EXAM}exam-mas-ii-modern-actuarial-statistics-ii` },
    factSources: [casOutline('MAS-II', 'https://www.casact.org/sites/default/files/2026-01/MASII_Content_Outlines_2026.pdf'), CAS_FEES],
  },
  'CAS-5': {
    facts: [CAS_LENGTH, CAS_DELIVERY, CAS_UPPER_ITEMS, CAS_UPPER_FEE],
    source: { label: 'CAS — Exam 5', url: `${CAS_EXAM}exam-5-basic-ratemaking-and-est-claim-liabilities` },
    factSources: [casOutline('Exam 5', 'https://www.casact.org/sites/default/files/2026-03/Exam_5_CO_2026_Fall.pdf'), CAS_FEES],
  },
  // Exam 6 is one exam key with a syllabus per nation, and CAS a page per
  // syllabus — so these two are keyed by the study guide (`GUIDE_ABOUT`).
  'CAS-7': {
    facts: [CAS_LENGTH, CAS_DELIVERY, CAS_UPPER_ITEMS, CAS_UPPER_FEE],
    source: { label: 'CAS — Exam 7', url: `${CAS_EXAM}exam-7-advanced-estimation-claims-liabilities` },
    factSources: [casOutline('Exam 7', 'https://www.casact.org/sites/default/files/2026-03/Exam_7_CO_2026_Fall.pdf'), CAS_FEES],
  },
  'CAS-8': {
    facts: [CAS_LENGTH, CAS_DELIVERY, CAS_UPPER_ITEMS, CAS_UPPER_FEE],
    source: { label: 'CAS — Exam 8', url: `${CAS_EXAM}exam-8-advanced-ratemaking` },
    factSources: [casOutline('Exam 8', 'https://www.casact.org/sites/default/files/2026-03/Exam_8_CO_2026_Fall.pdf'), CAS_FEES],
  },
  'CAS-9': {
    facts: [CAS_LENGTH, CAS_DELIVERY, CAS_UPPER_ITEMS, CAS_UPPER_FEE],
    source: { label: 'CAS — Exam 9', url: `${CAS_EXAM}exam-9-risk-management-actuaries` },
    factSources: [casOutline('Exam 9', 'https://www.casact.org/sites/default/files/2026-03/Exam_9_CO_2026_Fall.pdf'), CAS_FEES],
  },
  // PCPA is two parts, and the sittings are the project's: the exam is sat on
  // demand ("available continuously year-round"), and gates the project.
  'CAS-PCPA': {
    facts: [
      { label: 'Exam', value: '2 hours (2.5-hour appointment), 40 questions' },
      { label: 'Exam delivery', value: 'On demand, year-round, at Pearson VUE' },
      { label: 'Exam result', value: 'On screen at the test centre; final in the CAS portal 15 days later' },
      { label: 'Project', value: 'Remote, through the project portal; report of at most 1,250 words' },
      feeFact('CAS-PCPA'),
    ],
    source: CAS_PCPA,
    factSources: [CAS_PCPA_OUTLINE, CAS_FEES],
  },
  // The three DISCs: one CAS page, and no sittings.
  'CAS-DA': DISC,
  'CAS-IA': DISC,
  'CAS-RM': DISC,
}

const SOA_NO_SITTINGS = 'This exam’s sitting dates aren’t transcribed yet; its page on the SOA site has the schedule.'

/**
 * For an exam key shared by several study guides (Exam 6's Canada and US
 * syllabi), the publisher page for each guide — by wiki exam id (`6c-1`).
 */
// ── The non-exam requirements ─────────────────────────────────────────────────
// Read 2026-10-17 off each requirement's own page: the SOA's module, course and
// VEE pages, and the CAS's VEE and Course on Professionalism pages. None of
// them is sat at a sitting, so each says why there is none rather than leaving
// the panel to report a gap in a calendar.

const SOA_VEE: SittingSource = { label: 'SOA — Validation by Educational Experience', url: `${SOA_EXAM}edu-vee/` }
const CAS_VEE: SittingSource = { label: 'CAS — Validation by Educational Experience', url: 'https://www.casact.org/exams-admissions/validation-educational-experience' }
const VEE_GUIDELINES: SittingSource = { label: 'SOA — Guidelines for Approval of VEE Courses', url: 'https://www.soa.org/globalassets/assets/files/edu/edu-vee-guidelines.pdf' }
const VEE_FACTS = [
  { label: 'Credit', value: 'Approved university coursework or an approved alternative, not an exam' },
  { label: 'Apply', value: 'After passing two SOA, CAS or CIA actuarial exams' },
]
const VEE_NO_SITTINGS = 'A VEE is credit for coursework, so it has no sittings.'
const MODULE_NO_SITTINGS = 'An e-learning module is taken online, at the candidate’s own pace, so it has no sittings.'

function moduleAbout(name: string, page: string, objectives: string, facts: ExamAbout['facts'], noSittings = MODULE_NO_SITTINGS): ExamAbout {
  return {
    facts,
    source: { label: `SOA — ${name}`, url: `${SOA_EXAM}${page}` },
    factSources: [{ label: `SOA — ${name} module objectives`, url: objectives }],
    noSittings,
  }
}

// ── The CIA's own requirements ────────────────────────────────────────────────
// Read 2026-10-19 off the CIA's syllabi (each track's FCIA syllabus, and the
// ACIA syllabus for the modules and the Capstone) and its Becoming a Fellow /
// ACIA modules and Capstone Exam pages. The FCIA exam dates the CIA publishes
// aren't transcribed yet, so each says where they are.

const CIA_FELLOW: SittingSource = { label: 'CIA — Becoming a Fellow', url: 'https://www.cia-ica.ca/becoming-a-member/becoming-a-fellow/' }
const CIA_ACIA_PAGE: SittingSource = {
  label: 'CIA — ACIA modules and Capstone Exam',
  url: 'https://www.cia-ica.ca/becoming-a-member/becoming-an-associate/acia-modules-and-capstone-exam/',
}
const CIA_DL = 'https://www.cia-ica.ca/app/themes/wicket/custom/dl_file.php'
const CIA_TRACK_SYLLABI: Record<string, SittingSource> = {
  pc: { label: 'CIA — FCIA syllabus, property and casualty track', url: `${CIA_DL}?p=36231&fid=17019` },
  ila: { label: 'CIA — FCIA syllabus, individual life and annuities track', url: `${CIA_DL}?p=36227&fid=17011` },
  gb: { label: 'CIA — FCIA syllabus, group benefits track', url: `${CIA_DL}?p=36229&fid=17015` },
  ret: { label: 'CIA — FCIA syllabus, retirement benefits track', url: `${CIA_DL}?p=36233&fid=17023` },
  fie: { label: 'CIA — FCIA syllabus, finance, investments and ERM track', url: `${CIA_DL}?p=36225&fid=17007` },
}
const CIA_ACIA_SYLLABUS: SittingSource = { label: 'CIA — ACIA Capstone Exam and modules syllabus', url: `${CIA_DL}?p=608114&fid=627235` }

const CIA_EXAM_FACTS: ExamAbout['facts'] = [
  { label: 'Length', value: '4 hours' },
  { label: 'Format', value: 'Open book; written responses to questions set in the context of a problem' },
  { label: 'Delivery', value: 'Through the CIA’s learning management system' },
]
const CIA_MODULE_FACTS: ExamAbout['facts'] = [
  { label: 'Format', value: 'Online module in the CIA’s learning management system' },
  { label: 'Access', value: 'Six months from the date of purchase' },
  { label: 'Time', value: 'About 100 hours' },
]
const CIA_EXAM_DATES = 'The CIA publishes its FCIA exam dates on its Becoming a Fellow page, and they aren’t transcribed here yet.'
const CIA_MODULE_NO_SITTINGS = 'A module is taken online on the candidate’s own schedule, so it has no sittings.'

function ciaTrackAbout(track: string): Record<string, ExamAbout> {
  const syllabus = CIA_TRACK_SYLLABI[track]
  const exams = [`f1${track}-1`, `f2${track}-1`, `f3${track}-1`, ...(track === 'fie' ? ['f3bnk-1'] : [])]
  return {
    ...Object.fromEntries(exams.map(id => [id, {
      facts: CIA_EXAM_FACTS, source: CIA_FELLOW, factSources: [syllabus], noSittings: CIA_EXAM_DATES,
    }])),
    ...Object.fromEntries([`${track}-m1`, `${track}-m2`].map(id => [id, {
      facts: CIA_MODULE_FACTS, source: CIA_FELLOW, factSources: [syllabus], noSittings: CIA_MODULE_NO_SITTINGS,
    }])),
  }
}

const CIA_GUIDE_ABOUT: Record<string, ExamAbout> = {
  ...ciaTrackAbout('pc'),
  ...ciaTrackAbout('ila'),
  ...ciaTrackAbout('gb'),
  ...ciaTrackAbout('ret'),
  ...ciaTrackAbout('fie'),
  'acia-m1': {
    facts: [{ label: 'Format', value: 'Online module with recordings, short readings and quizzes, and a case study' }],
    source: CIA_ACIA_PAGE, factSources: [CIA_ACIA_SYLLABUS], noSittings: CIA_MODULE_NO_SITTINGS,
  },
  'acia-m2': {
    facts: [{ label: 'Format', value: 'Online module with recordings, short readings and quizzes, and a case study' }],
    source: CIA_ACIA_PAGE, factSources: [CIA_ACIA_SYLLABUS], noSittings: CIA_MODULE_NO_SITTINGS,
  },
  'acia-capstone': {
    facts: [
      { label: 'Length', value: '6 hours: a 4-hour common section and a 2-hour specialized section, long- or short-term' },
      { label: 'Format', value: 'Open book; written responses to tasks set in the context of a problem' },
      { label: 'Delivery', value: 'Through the CIA’s learning management system, with secure online proctoring' },
    ],
    source: CIA_ACIA_PAGE,
    factSources: [CIA_ACIA_SYLLABUS],
    noSittings: 'The CIA publishes the Capstone Exam’s dates on its ACIA pages, and they aren’t transcribed here yet.',
  },
}

const GUIDE_ABOUT: Record<string, ExamAbout> = {
  // The SOA exams past FM. The facts are transcribed from each exam's syllabus
  // (the PDF its study guide was converted from — data/examPdfLinks.ts); a
  // fellowship syllabus states no length or format, so those show none.
  // No sittings table holds them yet, so the panel says so.
  'vee-ms': { facts: VEE_FACTS, source: SOA_VEE, factSources: [VEE_GUIDELINES], noSittings: VEE_NO_SITTINGS },
  'vee-econ': { facts: VEE_FACTS, source: SOA_VEE, factSources: [VEE_GUIDELINES, CAS_VEE], noSittings: VEE_NO_SITTINGS },
  'vee-af': { facts: VEE_FACTS, source: SOA_VEE, factSources: [VEE_GUIDELINES, CAS_VEE], noSittings: VEE_NO_SITTINGS },
  'paf-1': moduleAbout('PAF', 'edu-exam-paf/', 'https://www.soa.org/globalassets/assets/files/edu/paf-objectives.pdf', [
    { label: 'Format', value: 'e-Learning module, with an end-of-module test and assessment' },
    { label: 'Access', value: '12 months from registration' },
    { label: 'Fee', value: '$224' },
  ]),
  'asf-1': moduleAbout('ASF', 'course-info/edu-exam-asf/', 'https://www.soa.org/globalassets/assets/files/edu/asf-objectives.pdf', [
    { label: 'Format', value: 'e-Learning module, with an end-of-module test and assessment' },
    { label: 'Access', value: '12 months from registration' },
    { label: 'Fee', value: '$224' },
  ]),
  'fap-1': moduleAbout('FAP', 'edu-module-fap-detail/', 'https://www.soa.org/globalassets/assets/files/edu/2023/fap-intro-objectives.pdf', [
    { label: 'Format', value: 'e-Learning course: modules with end-of-module assessments, then a final assessment' },
    { label: 'Access', value: '24 months from registration' },
    { label: 'Fees', value: '$551 for the modules and assessments; $1,316 per final assessment attempt' },
  ], 'FAP’s modules are taken online at the candidate’s own pace; its final assessment runs on a schedule the SOA posts separately, not transcribed here.'),
  'dmac-1': moduleAbout('DMAC', 'edu-module-dmac-detail/', 'https://www.soa.org/globalassets/assets/files/edu/dmac-objectives.pdf', [
    { label: 'Format', value: 'e-Learning module with a project component' },
    { label: 'Eligibility', value: 'Registration details are emailed on earning the ASA' },
    { label: 'Fee', value: '$337' },
  ]),
  'apc-1': {
    facts: [
      { label: 'Format', value: 'In person, or virtual: an e-Learning module and a case discussion' },
      { label: 'Eligibility', value: 'By invitation, once every other ASA requirement is complete' },
      { label: 'Fees', value: '$658 virtual; $847 in person' },
    ],
    source: { label: 'SOA — APC', url: `${SOA_EXAM}course-info/edu-apc-course-info/` },
    noSittings: 'The APC is by invitation, and its schedule is on the SOA’s APC page.',
  },
  'fac-1': {
    facts: [
      { label: 'Format', value: 'An e-Learning module, then a one-day virtual or two-day in-person meeting' },
      { label: 'Eligibility', value: 'By invitation, once every other FSA requirement is complete' },
      { label: 'Fees', value: '$2,305 virtual; $2,953 in person' },
    ],
    source: { label: 'SOA — FAC', url: `${SOA_EXAM}course-info/edu-fac-course-info/` },
    noSittings: 'The FAC is by invitation, and its schedule is on the SOA’s FAC page.',
  },
  'fam-1': {
    facts: [{ label: 'Length', value: '3.5 hours' }, { label: 'Questions', value: '34 multiple choice' }, SOA_DELIVERY, SOA_UNOFFICIAL],
    source: { label: 'SOA — Exam FAM', url: `${SOA_EXAM}edu-exam-fam/` },
    factSources: [{ label: 'SOA — Exam FAM syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2027/spring/syllabi/2027-2-fam-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'srm-1': {
    facts: [{ label: 'Length', value: '3.5 hours' }, { label: 'Questions', value: '35 multiple choice' }, SOA_DELIVERY, SOA_UNOFFICIAL],
    source: { label: 'SOA — Exam SRM', url: `${SOA_EXAM}edu-exam-srm-detail/` },
    factSources: [{ label: 'SOA — Exam SRM syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2027/spring/syllabi/2027-01-exam-srm-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'pa-1': {
    facts: [{ label: 'Length', value: '3.5 hours, no scheduled break' }, { label: 'Format', value: 'Written responses to tasks on a business problem, in Microsoft Word' }],
    source: { label: 'SOA — Exam PA', url: `${SOA_EXAM}edu-exam-pa-detail/` },
    factSources: [{ label: 'SOA — Exam PA syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/fall/syllabi/2026-10-exam-pa-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'altam-1': {
    facts: [{ label: 'Length', value: '3 hours' }, { label: 'Questions', value: 'Six questions, 60 points; one in an Excel workbook' }, { label: 'Delivery', value: 'Prometric test centre' }],
    source: { label: 'SOA — Exam ALTAM', url: `${SOA_EXAM}edu-exam-altam/` },
    factSources: [{ label: 'SOA — Exam ALTAM syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/fall/syllabi/2026-10-altam-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'astam-1': {
    facts: [{ label: 'Length', value: '3 hours' }, { label: 'Questions', value: 'Six questions, 60 points; one in an Excel workbook' }, { label: 'Delivery', value: 'Prometric test centre' }],
    source: { label: 'SOA — Exam ASTAM', url: `${SOA_EXAM}edu-exam-astam/` },
    factSources: [{ label: 'SOA — Exam ASTAM syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/astam/2026-fall-exam-astam-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'atpa-1': {
    facts: [{ label: 'Format', value: 'Take-home assessment: a report analysing a data set' }],
    source: { label: 'SOA — ATPA', url: `${SOA_EXAM}edu-exam-atpa/` },
    factSources: [{ label: 'SOA — ATPA syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2022/2022-01-atpa-learning-objectives.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'cfe-101': {
    facts: [],
    source: { label: 'SOA — CFE 101', url: `${SOA_EXAM}fsa2025/cfe-101/` },
    factSources: [{ label: 'SOA — CFE 101 syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/cfe-101/2026-11-cfe-101-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'cfe-201': {
    facts: [],
    source: { label: 'SOA — CFE 201', url: `${SOA_EXAM}fsa2025/cfe-201/` },
    factSources: [{ label: 'SOA — CFE 201 syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/cfe-201/2026-2027-cfe-201-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'cp-311': {
    facts: [],
    source: { label: 'SOA — CP 311', url: `${SOA_EXAM}fsa2025/cp-311/` },
    factSources: [{ label: 'SOA — CP 311 syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/cp-311/2026-2027-cp-311-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'cp-312': {
    facts: [],
    source: { label: 'SOA — CP 312', url: `${SOA_EXAM}fsa2025/cp-312/` },
    factSources: [{ label: 'SOA — CP 312 syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/cp-312/2026-2027-cp-312-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'cp-321': {
    facts: [],
    source: { label: 'SOA — CP 321', url: `${SOA_EXAM}fsa2025/cp-321/` },
    factSources: [{ label: 'SOA — CP 321 syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/cp-321/2026-2027-cp-321-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'cp-341': {
    facts: [],
    source: { label: 'SOA — CP 341', url: `${SOA_EXAM}fsa2025/cp-341/` },
    factSources: [{ label: 'SOA — CP 341 syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/cp-341/2026-2027-cp-341-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'cp-351': {
    facts: [],
    source: { label: 'SOA — CP 351', url: `${SOA_EXAM}fsa2025/cp-351/` },
    factSources: [{ label: 'SOA — CP 351 syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/cp-351/2026-2027-cp-351-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'gh-101': {
    facts: [],
    source: { label: 'SOA — GH 101', url: `${SOA_EXAM}fsa2025/gh-101/` },
    factSources: [{ label: 'SOA — GH 101 syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/gh-101/2026-2027-gh-101-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'gh-201c': {
    facts: [],
    source: { label: 'SOA — GH 201-C', url: `${SOA_EXAM}fsa2025/gh-201c/` },
    factSources: [{ label: 'SOA — GH 201-C syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/gh-201c/2026-2027-gh-201c-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'gh-201u': {
    facts: [],
    source: { label: 'SOA — GH 201-U', url: `${SOA_EXAM}fsa2025/gh-201u/` },
    factSources: [{ label: 'SOA — GH 201-U syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/gh-201u/2026-2027-gh-201u-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'gh-301': {
    facts: [],
    source: { label: 'SOA — GH 301', url: `${SOA_EXAM}fsa2025/gh-301/` },
    factSources: [{ label: 'SOA — GH 301 syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/gh-301/2026-2027-gh-301-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'gi-101': {
    facts: [],
    source: { label: 'SOA — GI 101', url: `${SOA_EXAM}fsa2025/gi-101/` },
    factSources: [{ label: 'SOA — GI 101 syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/gi-101/2026-2027-gi-101-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'gi-201': {
    facts: [],
    source: { label: 'SOA — GI 201', url: `${SOA_EXAM}fsa2025/gi-201/` },
    factSources: [{ label: 'SOA — GI 201 syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/gi-201/2026-2027-gi-201-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'gi-301': {
    facts: [],
    source: { label: 'SOA — GI 301', url: `${SOA_EXAM}fsa2025/gi-301/` },
    factSources: [{ label: 'SOA — GI 301 syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/gi-301/2027-03-gi-301-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'gi-302': {
    facts: [],
    source: { label: 'SOA — GI 302', url: `${SOA_EXAM}fsa2025/gi-302/` },
    factSources: [{ label: 'SOA — GI 302 syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/gi-302/2027-07-gi-302-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'ila-101': {
    facts: [],
    source: { label: 'SOA — ILA 101', url: `${SOA_EXAM}fsa2025/ila-101/` },
    factSources: [{ label: 'SOA — ILA 101 syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/ila-101/2026-2027-ila-101-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'ila-201i': {
    facts: [],
    source: { label: 'SOA — ILA 201-I', url: `${SOA_EXAM}fsa2025/ila-201i/` },
    factSources: [{ label: 'SOA — ILA 201-I syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/ila-201i/2026-2027-ila-201i-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'ila-201u': {
    facts: [],
    source: { label: 'SOA — ILA 201-U', url: `${SOA_EXAM}fsa2025/ila-201u/` },
    factSources: [{ label: 'SOA — ILA 201-U syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/ila-201u/2026-2027-ila-201u-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'inv-101': {
    facts: [],
    source: { label: 'SOA — INV 101', url: `${SOA_EXAM}fsa2025/inv-101/` },
    factSources: [{ label: 'SOA — INV 101 syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/inv-101/2026-2027-inv-101-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'inv-201': {
    facts: [],
    source: { label: 'SOA — INV 201', url: `${SOA_EXAM}fsa2025/inv-201/` },
    factSources: [{ label: 'SOA — INV 201 syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/inv-201/2026-2027-inv-201-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'ret-101': {
    facts: [],
    source: { label: 'SOA — RET 101', url: `${SOA_EXAM}fsa2025/ret-101/` },
    factSources: [{ label: 'SOA — RET 101 syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/ret-101/2026-2027-ret-101-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'ret-201': {
    facts: [],
    source: { label: 'SOA — RET 201', url: `${SOA_EXAM}fsa2025/ret-201/` },
    factSources: [{ label: 'SOA — RET 201 syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/ret-201/2026-2027-ret-201-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  'ret-301': {
    facts: [],
    source: { label: 'SOA — RET 301', url: `${SOA_EXAM}fsa2025/ret-301/` },
    factSources: [{ label: 'SOA — RET 301 syllabus', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/ret-301/2026-2027-ret-301-syllabus.pdf' }],
    noSittings: SOA_NO_SITTINGS,
  },
  '6c-1': {
    facts: [CAS_LENGTH, CAS_DELIVERY, CAS_UPPER_ITEMS, CAS_UPPER_FEE],
    source: { label: 'CAS — Exam 6 Canada', url: `${CAS_EXAM}exam-6c-regulation-and-financial-reporting-canada` },
    factSources: [casOutline('Exam 6C', 'https://www.casact.org/sites/default/files/2026-03/Exam_6C_CO_2026_Fall.pdf'), CAS_FEES],
  },
  '6u-1': {
    facts: [CAS_LENGTH, CAS_DELIVERY, CAS_UPPER_ITEMS, CAS_UPPER_FEE],
    source: { label: 'CAS — Exam 6 United States', url: `${CAS_EXAM}exam-6u-regulation-and-financial-reporting-us` },
    factSources: [casOutline('Exam 6U', 'https://www.casact.org/sites/default/files/2026-03/Exam_6U_CO_2026_Fall.pdf'), CAS_FEES],
  },
  '6i-1': {
    facts: [CAS_LENGTH, CAS_DELIVERY, CAS_UPPER_ITEMS, CAS_UPPER_FEE],
    source: { label: 'CAS — Exam 6 International', url: `${CAS_EXAM}exam-6-international` },
    factSources: [casOutline('Exam 6I', 'https://www.casact.org/sites/default/files/2026-03/Exam_6I_CO_2026_Fall.pdf'), CAS_FEES],
  },
  // 6T is credited from the AICT's own two exams, not sat at a CAS window: the
  // CAS-6 sittings the version menu lists are the other versions'.
  '6t-1': {
    facts: [
      { label: 'Sat as', value: 'The AICT’s Exams 6GA3 and 6GB3, administered by the AICT' },
      { label: 'Credit', value: 'Both passed after January 1, 2010; requested from the CAS in writing' },
    ],
    source: { label: 'CAS — Exam 6 Taipei', url: `${CAS_EXAM}exam-6t-regulation-and-financial-reportingtaipei` },
    factSources: [{ label: 'CAS — Exam 6-Taipei syllabus (2023)', url: 'https://www.casact.org/sites/default/files/2021-11/Exam6T_Syllabus.pdf' }],
  },
  'cop-1': {
    facts: [
      { label: 'Format', value: 'In person, with a virtual course in June and an Asia virtual course; e-modules and pre-work first' },
      { label: 'Offered', value: 'February, June, August and December; February and August only on demand' },
      { label: 'Places', value: 'First come, first served, by exam progress' },
    ],
    source: { label: 'CAS — Course on Professionalism', url: 'https://www.casact.org/exams-admissions/exams/acas-exams/course-professionalism' },
    noSittings: 'The CAS lists each Course on Professionalism on its own page, and none is transcribed here yet.',
  },
  ...CIA_GUIDE_ABOUT,
}

// ── Each sitting ──────────────────────────────────────────────────────────────
// Sittings from September 2026 on. Past sittings drop out of the version menu
// (`getSittingsForExam`), so nothing earlier is detailed.

/** CAS publishes no results date for an exam sitting, only a rule of thumb. */
const CAS_RESULTS_NOTE =
  'CAS publishes no results date for this sitting. Its candidate guide puts results 6–8 weeks after the window closes.'

/** CAS's October/November 2026 administration: one calendar, two windows. */
const CAS_OCT_2026: SittingMilestone[] = [
  { kind: 'registration-opens', label: 'Registration opens', date: '2026-07-15', note: '11:00 AM ET' },
  { kind: 'registration-deadline', label: 'Registration deadline', date: '2026-09-29', note: '11:59 PM ET' },
  { kind: 'deadline', label: 'Accommodation & holiday-extension requests', date: '2026-09-29' },
  { kind: 'deadline', label: 'Refund deadline', date: '2026-10-06' },
]

/**
 * SOA's CBT applications: "The latest a candidate can schedule a CBT
 * appointment is 48-hours prior to the start time of the exam on the final day
 * of the testing window."
 */
const SOA_CBT_BOOKING_NOTE =
  'Book a Prometric appointment no later than 48 hours before the start of the exam on the last day of the window.'

/** A 2027 SOA sitting: the schedule gives its deadline, with the time as printed. */
function soa2027(examId: string, startDate: string, deadline: string, time: string): SittingDetails {
  return {
    examIds: [examId],
    startDate,
    milestones: [{ kind: 'registration-deadline', label: 'Registration deadline', date: deadline, note: time }],
    sources: [SOA_SCHEDULE],
  }
}

export const SITTING_DETAILS: SittingDetails[] = [
  // ── CAS · October/November 2026 ───────────────────────────────────────────
  {
    examIds: ['CAS-5', 'CAS-6', 'CAS-7', 'CAS-8', 'CAS-9'],
    startDate: '2026-10-19',
    milestones: CAS_OCT_2026,
    notes: [CAS_RESULTS_NOTE],
    sources: [CAS_OCT_2026_REGISTRATION, CAS_SOBE],
  },
  {
    examIds: ['MAS-I', 'MAS-II'],
    startDate: '2026-10-28',
    milestones: CAS_OCT_2026,
    notes: [CAS_RESULTS_NOTE],
    sources: [CAS_OCT_2026_REGISTRATION, CAS_SOBE],
  },

  // ── CAS · PCPA projects ───────────────────────────────────────────────────
  {
    examIds: ['CAS-PCPA'],
    startDate: '2026-09-15',
    milestones: [
      { kind: 'registration-opens', label: 'Registration opens', date: '2026-07-29' },
      // "Candidates must pass the exam by August 25 to be eligible to register
      // for the September PCPA project." — the PCPA page.
      { kind: 'deadline', label: 'Exam deadline', date: '2026-08-25', note: 'Pass the exam by this date to register' },
      { kind: 'registration-deadline', label: 'Registration deadline', date: '2026-09-08' },
      { kind: 'deadline', label: 'Refund deadline', date: '2026-09-08' },
      { kind: 'deadline', label: 'Project submission deadline', date: '2026-09-30' },
      { kind: 'results', label: 'Results release', date: '2026-11-30', note: 'Subject to change' },
    ],
    notes: [
      'CAS’s 2026 Syllabus of Basic Education gives the project start as September 16; the PCPA page and content outline give September 15.',
    ],
    sources: [CAS_PCPA, CAS_PCPA_OUTLINE, CAS_SOBE],
  },
  {
    // The content outline's standing calendar, whose December row is the
    // Syllabus's 2026 December project (Dec 16 – 31, grades "February 28/29").
    // CAS has posted no December 2026 registration notice yet, and its notices
    // have moved the standing dates before (June 2026 closed June 1, not June
    // 8; December 2025 closed December 8, not December 9) — hence the note.
    examIds: ['CAS-PCPA'],
    startDate: '2026-12-16',
    milestones: [
      { kind: 'deadline', label: 'Exam deadline', date: '2026-11-25', note: 'Last day to sit the exam and still register' },
      { kind: 'registration-deadline', label: 'Registration deadline', date: '2026-12-09' },
      { kind: 'deadline', label: 'Project submission deadline', date: '2026-12-31' },
      { kind: 'results', label: 'Results release', date: '2027-02-28', note: 'Subject to change' },
    ],
    notes: [
      'From CAS’s standing PCPA calendar, which prints no year. CAS hasn’t posted registration for this project yet, and past notices have closed registration earlier than the calendar — check the PCPA page.',
    ],
    sources: [CAS_PCPA_OUTLINE, CAS_SOBE, CAS_PCPA],
  },

  // ── SOA · 2026 ────────────────────────────────────────────────────────────
  {
    examIds: ['FM'],
    startDate: '2026-10-01',
    format: 'CBT',
    milestones: [
      { kind: 'registration-deadline', label: 'Registration deadline', date: '2026-09-02' },
      { kind: 'results', label: 'Passing candidate numbers posted', date: '2026-12-11' },
      { kind: 'results', label: 'Transcripts released', date: '2026-12-14' },
    ],
    notes: [SOA_CBT_BOOKING_NOTE],
    sources: [
      { label: 'SOA — October 2026 FM application (CBT)', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/fall/registration/2026-10-fm-cbt-app.pdf' },
      SOA_RESULTS,
      SOA_SCHEDULE,
    ],
  },
  {
    examIds: ['FM'],
    startDate: '2026-10-01',
    format: 'P/P',
    milestones: [
      { kind: 'registration-deadline', label: 'Registration deadline', date: '2026-09-02' },
      { kind: 'results', label: 'Passing candidate numbers posted', date: '2026-12-11' },
      { kind: 'results', label: 'Transcripts released', date: '2026-12-14' },
    ],
    notes: ['The paper sitting runs 8:30 – 11:30 AM local time, at select international centres.'],
    sources: [
      { label: 'SOA — October 2026 FM application (paper)', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/fall/registration/2026-10-fm-pp-app.pdf' },
      SOA_RESULTS,
      SOA_SCHEDULE,
    ],
  },
  {
    examIds: ['P'],
    startDate: '2026-11-04',
    milestones: [
      // "registration closes at 10:00 AM U.S. CDT/CST on the posted date" —
      // SOA's registration page, for P and FM.
      { kind: 'registration-deadline', label: 'Registration deadline', date: '2026-09-30', note: '10:00 AM U.S. Central' },
      { kind: 'results', label: 'Passing candidate numbers posted', date: '2027-01-15' },
      { kind: 'results', label: 'Transcripts released', date: '2027-01-18' },
    ],
    notes: [SOA_CBT_BOOKING_NOTE],
    sources: [
      SOA_SCHEDULE,
      { label: 'SOA — November 2026 P application', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/fall/registration/2026-11-p-cbt-app.pdf' },
      SOA_RESULTS,
    ],
  },
  {
    examIds: ['FM'],
    startDate: '2026-12-03',
    milestones: [
      { kind: 'registration-deadline', label: 'Registration deadline', date: '2026-11-04', note: '10:00 AM U.S. Central' },
      // SOA's release schedule prints this sitting's candidate-number date as
      // "February 5, 2026" — before the exam, so a slip on SOA's page. It is
      // left out rather than corrected into a date SOA never printed.
      { kind: 'results', label: 'Transcripts released', date: '2027-02-08' },
    ],
    notes: [SOA_CBT_BOOKING_NOTE],
    sources: [
      SOA_SCHEDULE,
      { label: 'SOA — December 2026 FM application', url: 'https://www.soa.org/globalassets/assets/files/edu/2026/fall/registration/2026-12-fm-cbt-fill-app.pdf' },
      SOA_RESULTS,
    ],
  },

  // ── SOA · 2027 — no results dates are published yet ──────────────────────
  // The Exam P page, read 2026-10-02, under "Ready to Register?": "Closed —
  // Registration opens week of October 26, 2026". The SOA names a week, not
  // a day: the date is the week's first day, and the label says so.
  {
    examIds: ['P'],
    startDate: '2027-01-14',
    milestones: [
      { kind: 'registration-opens', label: 'Registration opens the week of', date: '2026-10-26' },
      { kind: 'registration-deadline', label: 'Registration deadline', date: '2026-12-15', note: '11:59 PM' },
    ],
    sources: [SOA_SCHEDULE, { label: 'SOA — Exam P', url: `${SOA_EXAM}edu-exam-p-detail/` }],
  },
  soa2027('FM', '2027-02-04', '2027-01-05', '11:59 PM'),
  // Printed "11:59 AM", where every other 2027 deadline says PM — kept as printed.
  soa2027('P', '2027-03-01', '2027-02-02', '11:59 AM'),
  soa2027('FM', '2027-04-01', '2027-03-02', '11:59 PM'),
  soa2027('P', '2027-05-07', '2027-04-06', '11:59 PM'),
  soa2027('FM', '2027-06-10', '2027-05-11', '11:59 PM'),
  soa2027('P', '2027-07-01', '2027-06-01', '11:59 PM'),
  soa2027('FM', '2027-08-05', '2027-07-06', '11:59 PM'),
  soa2027('P', '2027-09-16', '2027-08-17', '11:59 PM'),
  soa2027('FM', '2027-10-01', '2027-08-31', '11:59 PM'),
  soa2027('P', '2027-11-03', '2027-10-05', '11:59 PM'),
  soa2027('FM', '2027-12-02', '2027-11-02', '11:59 PM'),
]

/** The transcribed details for one sitting of one exam, or null. */
export function sittingDetailsFor(examId: string, sitting: ExamSitting): SittingDetails | null {
  return SITTING_DETAILS.find(d =>
    d.examIds.includes(examId)
    && d.startDate === sitting.startDate
    && (!d.format || d.format === sitting.format),
  ) ?? null
}

/**
 * How an exam is sat, and the publisher's page for it, or null — the study
 * guide's own entry first (`wikiExamId`, e.g. `6u-1`), then the exam key's.
 */
export function examAbout(examId: string, wikiExamId?: string): ExamAbout | null {
  return (wikiExamId ? GUIDE_ABOUT[wikiExamId.toLowerCase()] : undefined) ?? EXAM_ABOUT[examId] ?? null
}
